/** What one chunk of the body produced. */
export interface SseChunkResult {
  /** Lines that ended inside this chunk (comments and blank lines included). */
  completedLines: number;
  /** Events of the default `message` type dispatched inside this chunk. */
  messageEvents: number;
}

export interface SseEventParser {
  push(chunk: string): SseChunkResult;
}

/**
 * Reads already-decoded text of a `text/event-stream` body, chunk by chunk, applying the line rules
 * `EventSource` applied: a line ends at `\n`, `\r\n` or `\r` (even with the `\r` and the `\n` in
 * different chunks); a line starting with `:` is a comment; a blank line dispatches the pending
 * event only when it had a `data` field and its type is empty or `message` — the only events
 * `EventSource.onmessage` ever received. `id` and `retry` are ignored: the server sends neither.
 */
export function createSseEventParser(): SseEventParser {
  let line = '';
  let hasData = false;
  let eventType = '';
  let lastWasCarriageReturn = false;

  /** Returns whether the line dispatched a message event. */
  function completeLine(completed: string): boolean {
    if (completed === '') {
      const dispatched = hasData && (eventType === '' || eventType === 'message');
      hasData = false;
      eventType = '';
      return dispatched;
    }
    const colon = completed.indexOf(':');
    const field = colon === -1 ? completed : completed.slice(0, colon);
    const rawValue = colon === -1 ? '' : completed.slice(colon + 1);
    if (field === 'data') {
      hasData = true;
    } else if (field === 'event') {
      eventType = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;
    }
    return false;
  }

  return {
    push(chunk: string): SseChunkResult {
      let completedLines = 0;
      let messageEvents = 0;
      for (const char of chunk) {
        if (lastWasCarriageReturn && char === '\n') {
          lastWasCarriageReturn = false;
          continue;
        }
        lastWasCarriageReturn = char === '\r';
        if (char !== '\n' && char !== '\r') {
          line += char;
          continue;
        }
        completedLines++;
        const completed = line;
        line = '';
        if (completeLine(completed)) {
          messageEvents++;
        }
      }
      return { completedLines, messageEvents };
    },
  };
}
